import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkflow, useRunWorkflow, useExtractWorkflow, useExecutions, useDeleteExecution } from '@/hooks/useDocFlow';
import DriveBrowser from './DriveBrowser';
import ApprovalDialog from './ApprovalDialog';
import { EXECUTION_STATUS_LABELS, EXECUTION_STATUS_COLORS, DRIVE_ROOT_FOLDER_ID } from '@/types/docflow';
import type { IDriveFile } from '@/types/docflow';

const WorkflowRunPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('docflow');
  const { data: workflow, isLoading: wfLoading } = useWorkflow(id || '');
  const { data: executions } = useExecutions(id || '');
  const runWorkflow = useRunWorkflow();
  const extractWorkflow = useExtractWorkflow();
  const deleteExecution = useDeleteExecution();

  const [showBrowser, setShowBrowser] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<IDriveFile[]>([]);
  const [approvalExecId, setApprovalExecId] = useState<string | null>(null);
  const [deletingExecId, setDeletingExecId] = useState<string | null>(null);

  // Find drive_input node's folder ID
  const driveNode = workflow?.nodes?.find((n) => n.type === 'drive_input');
  const rootFolderId = driveNode?.config?.folder_id || DRIVE_ROOT_FOLDER_ID;

  // Check if workflow has a sheet_output node
  const hasSheetOutput = useMemo(
    () => workflow?.nodes?.some((n) => n.type === 'sheet_output' && n.config?.spreadsheet_id) ?? false,
    [workflow]
  );

  const handleSelectFiles = useCallback((files: IDriveFile[]) => {
    setSelectedFiles(files);
  }, []);

  const handleRun = useCallback(() => {
    if (!id || selectedFiles.length === 0) return;
    const fileIds = selectedFiles.map((f) => f.id);

    if (hasSheetOutput) {
      // Extract & review flow: extract data, then show approval dialog
      extractWorkflow.mutate(
        { id, fileIds },
        {
          onSuccess: (exec) => {
            setApprovalExecId(exec.id);
          },
        }
      );
    } else {
      // Direct run: no sheet output, go straight to results
      runWorkflow.mutate(
        { id, fileIds },
        {
          onSuccess: (exec) => {
            navigate(`/app/docflow/executions/${exec.id}`);
          },
        }
      );
    }
  }, [id, selectedFiles, hasSheetOutput, extractWorkflow, runWorkflow, navigate]);

  const handleApproved = useCallback(() => {
    if (approvalExecId) {
      navigate(`/app/docflow/executions/${approvalExecId}`);
    }
    setApprovalExecId(null);
  }, [approvalExecId, navigate]);

  if (wfLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{t('error.notFound')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/app/docflow')}
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('execution.title')}: {workflow.name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{workflow.description}</p>
        </div>
      </div>

      {/* Workflow summary */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Workflow Nodes</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {workflow.nodes?.map((node, idx) => (
            <div key={node.id} className="flex items-center gap-2">
              {idx > 0 && (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
              <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded dark:text-gray-300">
                {node.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* File selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('drive.selectFiles')}
          </h3>
          <button
            onClick={() => setShowBrowser(true)}
            className="text-sm px-3 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800"
          >
            {t('config.browseFolders')}
          </button>
        </div>

        {selectedFiles.length === 0 ? (
          <div className="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded">
            No files selected. Click "Browse Folders" to pick files.
          </div>
        ) : (
          <div className="space-y-1">
            {selectedFiles.map((file) => (
              <div key={file.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-1">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span className="truncate">{file.name}</span>
                <button
                  onClick={() => setSelectedFiles((prev) => prev.filter((f) => f.id !== file.id))}
                  className="ml-auto text-red-400 hover:text-red-600 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Run button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleRun}
            disabled={selectedFiles.length === 0 || runWorkflow.isLoading || extractWorkflow.isLoading}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(runWorkflow.isLoading || extractWorkflow.isLoading)
              ? (hasSheetOutput ? t('execution.extracting') : t('execution.running'))
              : (hasSheetOutput ? t('execution.extractAndReview') : t('execution.runWorkflow'))}
          </button>
        </div>
      </div>

      {/* Execution history */}
      {executions && executions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('execution.history')}
          </h3>
          <div className="space-y-2">
            {executions.map((exec) => (
              <div
                key={exec.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <div
                  className="flex items-center gap-3 flex-1 min-w-0"
                  onClick={() => navigate(`/app/docflow/executions/${exec.id}`)}
                >
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${EXECUTION_STATUS_COLORS[exec.status]}`}>
                    {EXECUTION_STATUS_LABELS[exec.status]}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {exec.done_files}/{exec.total_files} files
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-400">
                    {new Date(exec.created_at).toLocaleString()}
                  </span>
                  {deletingExecId === exec.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteExecution.mutate(
                            { id: exec.id, workflowId: exec.workflow_id },
                            { onSettled: () => setDeletingExecId(null) }
                          );
                        }}
                        className="text-xs px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        {t('workflow.delete')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingExecId(null);
                        }}
                        className="text-xs px-2 py-0.5 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                      >
                        {t('execution.cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingExecId(exec.id);
                      }}
                      className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1"
                      title={t('workflow.delete')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drive browser modal */}
      {showBrowser && (
        <DriveBrowser
          isOpen={showBrowser}
          onClose={() => setShowBrowser(false)}
          mode="files"
          rootFolderId={rootFolderId}
          onSelectFiles={handleSelectFiles}
        />
      )}

      {/* Approval dialog */}
      {approvalExecId && workflow && (
        <ApprovalDialog
          isOpen={!!approvalExecId}
          onClose={() => setApprovalExecId(null)}
          executionId={approvalExecId}
          workflow={workflow}
          onApproved={handleApproved}
        />
      )}
    </div>
  );
};

export default WorkflowRunPage;
